import React, { useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { FaChartBar, FaChartPie, FaPhoneAlt } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import Form from "react-bootstrap/Form";
import { Button } from "primereact/button";
import Chart from "react-apexcharts";

const dateToInputStr = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

/* ─── Color palettes ─────────────────────────────────────────────────────── */
const AGENT_COLORS    = ["#6366f1", "#f43f5e", "#0ea5e9"];
const INCOMING_COLORS = ["#f97316", "#10b981", "#a855f7"];
const OUTGOING_COLORS = ["#ef4444", "#eab308", "#22c55e"];

/* ─── Legend pill ────────────────────────────────────────────────────────── */
const LegendDot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
    }}
  />
);

const LegendRow = ({ items }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "6px 14px",
      marginBottom: 8,
    }}
  >
    {items.map(({ color, label }) => (
      <span
        key={label}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 11,
          color: "var(--dash-text-soft)",
          fontWeight: 500,
          letterSpacing: "0.01em",
        }}
      >
        <LegendDot color={color} />
        {label}
      </span>
    ))}
  </div>
);

/* ─── Toggle icon button ─────────────────────────────────────────────────── */
const ToggleBtn = ({ isBar, onToggle }) => (
  <button
    onClick={onToggle}
    title={isBar ? "Switch to donut view" : "Switch to bar view"}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: 999,
      border: "1px solid var(--dash-border)",
      background: "var(--dash-surface-alt, var(--dash-surface))",
      color: "var(--dash-primary)",
      cursor: "pointer",
      transition: "background 0.18s, box-shadow 0.18s",
      flexShrink: 0,
      outline: "none",
    }}
    onMouseEnter={e => {
      e.currentTarget.style.background = "var(--dash-primary-soft)";
      e.currentTarget.style.boxShadow = "0 0 0 3px var(--dash-primary-soft)";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.background = "var(--dash-surface-alt, var(--dash-surface))";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    {isBar ? <FaChartPie size={14} /> : <FaChartBar size={14} />}
  </button>
);

/* ─── Empty state ────────────────────────────────────────────────────────── */
const EmptyState = ({ label }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      height: "100%",
      minHeight: 120,
    }}
  >
    {/* Ghost donut ring */}
    <div
      style={{
        position: "relative",
        width: 88,
        height: 88,
        borderRadius: "50%",
        background:
          "conic-gradient(rgba(99,102,241,0.18) 0% 40%, rgba(148,163,184,0.12) 40% 70%, rgba(99,102,241,0.10) 70% 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "var(--dash-surface)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--dash-text-soft)",
        }}
      >
        <FaPhoneAlt size={18} style={{ opacity: 0.4 }} />
      </div>
    </div>
    <span
      style={{
        fontSize: 12,
        color: "var(--dash-text-soft)",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  </div>
);

/* ─── Card shell ─────────────────────────────────────────────────────────── */
const ChartCard = ({ title, isBar, onToggle, legend, children, style }) => (
  <div
    className="chart-block"
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      ...style,
    }}
  >
    {/* Header */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <span
        style={{
          fontWeight: 700,
          fontSize: 14,
          color: "var(--dash-text)",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </span>
      <ToggleBtn isBar={isBar} onToggle={onToggle} />
    </div>

    {/* Legend */}
    {legend}

    {/* Chart area */}
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Main component
═══════════════════════════════════════════════════════════════════════════ */
const DashboardCallsCharts = ({
  barAgentsData,
  doughnutData1,
  barIncomingData,
  incomingData,
  isIncomingDataEmpty,
  barOutgoingData,
  outgoingDataDoughnut,
  isOutgoingDataEmpty,
  startDate,
  endDate,
  handleDashboardStartDateChange,
  handleDashboardEndDateChange,
  dashboardStartCalendarMinDate,
  dashboardStartCalendarMaxDate,
  dashboardEndCalendarMinDate,
  dashboardEndCalendarMaxDate,
  handleFilter,
  onRefreshCalls,
}) => {
  const [ToggleincomingBarChart, setToggleincomingBarChart] = useState(false);
  const [ToggleOutgoingBarChart, setToggleOutgoingBarChart] = useState(false);
  const [ToggleAgentBarChart,    setToggleAgentBarChart]    = useState(false);
  const [ToggleAgentPieChart,    setToggleAgentPieChart]    = useState(true);

  /* Shared donut props */
  const donutProps = {
    cx: "50%",
    cy: "50%",
    innerRadius: 38,
    outerRadius: 60,
    paddingAngle: 3,
    dataKey: "value",
    nameKey: "name",
  };

  return (
    <div className="container-all" style={{ width: "100%", overflowX: "hidden" }}>

      {/* ── Agents card ───────────────────────────────────────────────────── */}
      <div className="agent-container">
        <ChartCard
          title="Agents"
          isBar={ToggleAgentBarChart}
          onToggle={() => {
            setToggleAgentBarChart(prev => !prev);
            setToggleAgentPieChart(prev => !prev);
          }}
          legend={
            <LegendRow
              items={[
                { color: AGENT_COLORS[0], label: "Total" },
                { color: AGENT_COLORS[1], label: "Logged in" },
                { color: AGENT_COLORS[2], label: "On call" },
              ]}
            />
          }
          style={{ height: "100%" }}
        >
          <div className="chart-wrapper-agent" style={{ width: "100%" }}>
            {ToggleAgentBarChart ? (
              <Chart
                options={barAgentsData.options}
                series={barAgentsData.series}
                type="bar"
                height={220}
                width="100%"
              />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie {...donutProps} data={doughnutData1}>
                    {doughnutData1.map((_, idx) => (
                      <Cell key={`agent-${idx}`} fill={AGENT_COLORS[idx % AGENT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "var(--dash-surface)",
                      border: "1px solid var(--dash-border)",
                      borderRadius: 10,
                      color: "var(--dash-text)",
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>
      </div>

      {/* ── Calls card ────────────────────────────────────────────────────── */}
      <div className="call-container">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: "var(--dash-text)",
              letterSpacing: "-0.01em",
            }}
          >
            Agent Incoming & Outgoing
          </span>
          <button
            type="button"
            onClick={onRefreshCalls}
            style={{
              border: "1px solid var(--dash-border)",
              background: "var(--dash-surface-alt)",
              color: "var(--dash-text-soft)",
              borderRadius: 999,
              width: 30,
              height: 30,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            title="Refresh Incoming & Outgoing"
            aria-label="Refresh Incoming & Outgoing"
          >
            <FiRefreshCw size={13} />
          </button>
        </div>

        {/* Date filter toolbar */}
        <div className="dash-cal">
          <div className="calender-main">
            <Form.Control
              type="date"
              value={dateToInputStr(startDate)}
              onChange={(e) =>
                handleDashboardStartDateChange({
                  value: inputStrToLocalDate(e.target.value),
                })
              }
              placeholder="Start Date"
              min={dateToInputStr(dashboardStartCalendarMinDate)}
              max={dateToInputStr(dashboardStartCalendarMaxDate)}
              className="calender"
            />
            <span
              style={{
                fontSize: 12,
                color: "var(--dash-text-soft)",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              to
            </span>
            <Form.Control
              type="date"
              value={dateToInputStr(endDate)}
              onChange={(e) =>
                handleDashboardEndDateChange({
                  value: inputStrToLocalDate(e.target.value),
                })
              }
              placeholder="End Date"
              min={dateToInputStr(dashboardEndCalendarMinDate)}
              max={dateToInputStr(dashboardEndCalendarMaxDate)}
              className="calender"
            />
            <Button label="Filter" onClick={handleFilter} className="filter" />
          </div>
        </div>

        {/* Incoming + Outgoing side-by-side */}
        <div className="inside-call-container">

          {/* Incoming */}
          <ChartCard
            title="Incoming Calls"
            isBar={ToggleincomingBarChart}
            onToggle={() => setToggleincomingBarChart(prev => !prev)}
            legend={
              <LegendRow
                items={[
                  { color: INCOMING_COLORS[0], label: "Dialed" },
                  { color: INCOMING_COLORS[1], label: "Answered" },
                  { color: INCOMING_COLORS[2], label: "Abandoned" },
                ]}
              />
            }
          >
            <div className="chart-wrapper" style={{ width: "100%", height: 180 }}>
              {ToggleincomingBarChart ? (
                <Chart
                  options={barIncomingData.options}
                  series={barIncomingData.series}
                  type="bar"
                  height={200}
                  width="100%"
                />
              ) : isIncomingDataEmpty ? (
                <EmptyState label="No incoming calls" />
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie {...donutProps} data={incomingData}>
                      {incomingData.map((_, idx) => (
                        <Cell key={`inc-${idx}`} fill={INCOMING_COLORS[idx % INCOMING_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "var(--dash-surface)",
                        border: "1px solid var(--dash-border)",
                        borderRadius: 10,
                        color: "var(--dash-text)",
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </ChartCard>

          {/* Outgoing */}
          <ChartCard
            title="Outgoing Calls"
            isBar={ToggleOutgoingBarChart}
            onToggle={() => setToggleOutgoingBarChart(prev => !prev)}
            legend={
              <LegendRow
                items={[
                  { color: OUTGOING_COLORS[0], label: "Total" },
                  { color: OUTGOING_COLORS[1], label: "Dialed" },
                  { color: OUTGOING_COLORS[2], label: "Answered" },
                ]}
              />
            }
          >
            <div className="chart-wrapper" style={{ width: "100%", height: 180 }}>
              {ToggleOutgoingBarChart ? (
                <Chart
                  options={barOutgoingData.options}
                  series={barOutgoingData.series}
                  type="bar"
                  height={200}
                  width="100%"
                />
              ) : isOutgoingDataEmpty ? (
                <EmptyState label="No outgoing calls" />
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie {...donutProps} data={outgoingDataDoughnut}>
                      {outgoingDataDoughnut.map((_, idx) => (
                        <Cell key={`out-${idx}`} fill={OUTGOING_COLORS[idx % OUTGOING_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "var(--dash-surface)",
                        border: "1px solid var(--dash-border)",
                        borderRadius: 10,
                        color: "var(--dash-text)",
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </ChartCard>

        </div>
      </div>
    </div>
  );
};

export default DashboardCallsCharts;
